import { useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import { EmptyField } from 'components/elements/EmptyField';
import Tooltip from 'components/elements/Tooltip';
import { FlexRow } from 'components/layout/FlexRow';
import { useZones } from 'state/projectTypes';
import { Project } from 'types';

type Props = {
  lud: Project['land_use_redesignation'];
  code: string;
  cityId: string;
};

export const Lud = ({ lud, code, cityId }: Props) => {
  const fromLud = lud?.fromlud || '';
  const fromLudEntries = Array.from(new Set(fromLud.split(';'))).filter(Boolean);
  const { getAllCityLuds } = useZones(cityId);

  const getZoneDescriptionWithCode = useCallback(
    (zoneCode: string) => {
      const zoneDescription = getAllCityLuds()
        .filter((ludObj) => ludObj.code === zoneCode)
        .map((ludObj) => ludObj.description);

      return zoneDescription.length > 0 ? zoneDescription[0] : zoneCode;
    },
    [getAllCityLuds],
  );

  const getParentZoneWithCode = useCallback(
    (zoneCode: string) => {
      const parentZone = getAllCityLuds()
        .filter((ludObj) => ludObj.code.toLowerCase() === zoneCode.toLowerCase())
        .map((ludObj) => ludObj.district);
      const result = parentZone.length > 0 ? parentZone[0] : zoneCode;

      return result;
    },
    [getAllCityLuds],
  );

  const parentContainer = {
    justifyContent: 'left',
  };

  const ludContainer = {
    maxWidth: '500px',
    width: '100%',
    flexWrap: 'wrap',
    justifyContent: 'left',
  };

  const fromLudText = { textTransform: 'capitalize' };
  const ludContainerText = {
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '4px',
  };

  if ((!lud?.fromlud || !lud.proposedlud) && code) {
    return (
      <Tooltip
        tooltipText={getZoneDescriptionWithCode(code)}
        placement="bottom"
        isDisabled={getZoneDescriptionWithCode(code) === code}
      >
        <Box sx={{ ...fromLudText, ...ludContainerText }}>
          {getParentZoneWithCode(code) !== code ? (
            <>
              <Text>{capitalize(getParentZoneWithCode(code))}</Text>
              <Text>{`(${code})`}</Text>
            </>
          ) : (
            <EmptyField />
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <FlexRow sx={parentContainer} gridGap={1}>
      <FlexRow sx={ludContainer}>
        {fromLudEntries.length > 0 ? (
          fromLudEntries.map((entry, index) => {
            const zoneDescription = getZoneDescriptionWithCode(entry);
            const parentZoneDescription = getParentZoneWithCode(entry);
            const isEntryUnique =
              !!entry &&
              !!parentZoneDescription &&
              !parentZoneDescription.includes(entry);

            return (
              <Tooltip
                key={entry}
                tooltipText={zoneDescription}
                placement="bottom"
                isDisabled={zoneDescription === entry}
              >
                <Text sx={fromLudText}>
                  {capitalize(parentZoneDescription)}
                  {isEntryUnique && ` (${entry})`}
                  {index !== fromLudEntries.length - 1 && ', '}
                </Text>
              </Tooltip>
            );
          })
        ) : (
          <EmptyField />
        )}
      </FlexRow>
    </FlexRow>
  );
};
